import { useCheckoutStore } from '@/stores/checkoutStore';
import { useSnackbarStore } from '@/stores/snackbarInfo';
import isEmpty from 'lodash/isEmpty';
import { computed, reactive, ref, watch } from 'vue';

export function useEventTableRegistration(
  allTicketSelectionData,
  loadingTicketData,
  addonSummary,
  postOrderDetails,
  route,
) {
  const checkoutStore = useCheckoutStore();
  const snackbar = useSnackbarStore();

  const localSelectionsMap = reactive({});
  const orderedTableInstances = ref([]);
  const registrations = ref({});
  const stashedData = reactive({});
  const occupiedAddons = ref({});
  const primaryRegistrantSelectionKey = ref(null);
  const pendingQtyChange = ref(null);
  const openDeleteStashedFormConfirm = ref(false);
  const additionalTableGuests = ref([]);
  const tableGuestCounter = ref(0);
  const instanceCounter = ref(0);
  const pendingDropdownChange = ref(null);

  const tables = computed(() => orderedTableInstances.value);

  const selectedTableTypes = computed(() => {
    return Object.values(localSelectionsMap).filter((item) => item.qty > 0);
  });

  const hasMultipleTableTypes = computed(() => selectedTableTypes.value.length > 1);

  const assignedTableFormsCountPerType = computed(() => {
    const counts = {};

    const primaryTicket = primaryRegistrantTable.value;
    if (primaryTicket) {
      const primaryKey = primaryRegistrantSelectionKey.value;
      if (primaryKey) {
        counts[primaryKey] = (counts[primaryKey] || 0) + 1;
      }
    }

    additionalTableGuests.value.forEach((guest) => {
      const key = hasMultipleTableTypes.value
        ? guest.selectedTicketKey
        : primaryRegistrantSelectionKey.value;
      if (key) {
        counts[key] = (counts[key] || 0) + 1;
      }
    });
    return counts;
  });

  const tableGuestDropdownItems = computed(() => {
    return selectedTableTypes.value.map((item) => {
      const key = `${item.ticket.table_id}_${item.ticket.ticket_id}`;
      const totalQty = item.qty;
      const totalSeatsForType = (item.ticket?.included_ticket_count || 0) * totalQty;
      const assignedQty = assignedTableFormsCountPerType.value[key] || 0;
      const availableQty = totalSeatsForType - assignedQty;
      return {
        title: `${item.ticket?.name} (${availableQty} seats available)`,
        value: key,
        disabled: availableQty <= 0,
      };
    });
  });

  const primaryRegistrantTable = computed(() => {
    if (!tables.value || tables.value.length === 0) return null;
    if (!hasMultipleTableTypes.value || !primaryRegistrantSelectionKey.value) {
      return tables.value[0];
    }
    return tables.value.find(
      (t) => `${t.table_id}_${t.ticket_id}` === primaryRegistrantSelectionKey.value,
    );
  });

  const displayAddOnSummary = computed(() =>
    Object.values(registrations.value).reduce((acc, curr) => {
      if (curr) {
        curr?.add_ons?.forEach((a) => {
          acc[a.name] = {
            total:
              (acc[a.name]?.total || 0) +
              (a?.variant ? Number(a.variant.price ?? a?.price ?? 0) : (a?.price ?? 0)),
            count: (acc[a.name]?.count || 0) + 1,
          };
        });
      }
      return acc;
    }, {}),
  );

  const displayTableSummary = computed(() => {
    const allSelections = Object.values(localSelectionsMap)
      .filter((item) => item.qty > 0)
      .map((item) => ({
        ticket: item.ticket,
        qty: item.qty,
      }));

    return allSelections.map((selection) => {
      let displayName = `${selection.ticket.name} (x${selection.qty})`;
      let price = selection?.ticket?.config?.price * selection.qty;
      let originalPrice = selection?.ticket?.config?.price * selection.qty;

      if (selection?.ticket?.is_early_bird && selection?.ticket?.early_bird_amount) {
        price = selection?.ticket?.early_bird_amount * selection.qty;
      }
      let includedTicketCount;
      if (
        selection?.ticket?.table_id &&
        selection?.ticket?.included_ticket_count &&
        selection?.ticket?.included_ticket_count > 0
      ) {
        includedTicketCount = selection.qty * (selection?.ticket?.included_ticket_count || 0);
      }
      return {
        displayName,
        price,
        originalPrice,
        id: selection.ticket.ticket_id,
        isEarlyBird: selection?.ticket?.is_early_bird && selection?.ticket?.is_early_bird_active,
        isDonation: selection?.ticket?.price_type === 'donation',
        includedTicketCount,
      };
    });
  });

  const totalTableSeats = computed(() => {
    return Object.values(localSelectionsMap).reduce((acc, item) => {
      if (item.qty > 0 && item.ticket.table_id) {
        return acc + (item.ticket.included_ticket_count || 0) * item.qty;
      }
      return acc;
    }, 0);
  });

  const totalActiveTableForms = computed(() => {
    if (tables.value.length === 0) return 0;
    return 1 + additionalTableGuests.value.length;
  });
  const remainingTableSeats = computed(() => {
    if (totalTableSeats.value === 0) return 0;
    return totalTableSeats.value - totalActiveTableForms.value;
  });

  const disableContinue = computed(() => {
    if (!tables.value || tables.value.length === 0) {
      return true;
    }
    const primaryTicket = primaryRegistrantTable.value;
    if (!primaryTicket) return true;
    const primaryStash = stashedData[primaryTicket._instanceId];
    const isPrimaryFilled = primaryStash && Object.keys(primaryStash).length > 0;

    if (!isPrimaryFilled) return true;

    const allTableGuestsFilled = additionalTableGuests.value.every(
      (guest) =>
        !!stashedData[guest._instanceId] && Object.keys(stashedData[guest._instanceId]).length > 0,
    );
    if (!allTableGuestsFilled) return true;
    if (remainingTableSeats.value < 0) return true;

    return false;
  });

  function clearLocalState() {
    Object.keys(registrations.value).forEach((key) => delete registrations.value[key]);
    Object.keys(stashedData).forEach((key) => delete stashedData[key]);
    orderedTableInstances.value = [];
    Object.keys(localSelectionsMap).forEach((key) => {
      if (localSelectionsMap[key]) {
        localSelectionsMap[key].qty = 0;
      }
    });
    primaryRegistrantSelectionKey.value = null;
    pendingQtyChange.value = null;
    occupiedAddons.value = {};
    additionalTableGuests.value = [];
    tableGuestCounter.value = 0;
    instanceCounter.value = 0;
    pendingDropdownChange.value = null;
  }

  function findBestTableInstanceToRemove(ticketTypeIdKey) {
    const instances = tables.value.filter(
      (t) => `${t.table_id}_${t.ticket_id}` === ticketTypeIdKey,
    );
    if (!instances.length) return null;

    const primaryInstanceId = primaryRegistrantTable.value?._instanceId;
    const lastEmptyInstance = [...instances].reverse().find((t) => {
      const stash = stashedData[t._instanceId];
      return (!stash || Object.keys(stash).length === 0) && t._instanceId !== primaryInstanceId;
    });
    if (lastEmptyInstance) {
      return { id: lastEmptyInstance._instanceId, needsConfirm: false, isPrimaryProtected: false };
    }

    const lastFilledNonPrimary = [...instances].reverse().find((t) => {
      const stash = stashedData[t._instanceId];
      return stash && Object.keys(stash).length > 0 && t._instanceId !== primaryInstanceId;
    });
    if (lastFilledNonPrimary) {
      return {
        id: lastFilledNonPrimary._instanceId,
        needsConfirm: true,
        isPrimaryProtected: false,
      };
    }

    const primaryInstance = instances.find((t) => t._instanceId === primaryInstanceId);
    if (primaryInstance) {
      const isFilled =
        stashedData[primaryInstance._instanceId] &&
        Object.keys(stashedData[primaryInstance._instanceId]).length > 0;
      return { id: primaryInstance._instanceId, needsConfirm: isFilled, isPrimaryProtected: true };
    }
    const lastInstance = instances[instances.length - 1];
    if (lastInstance) {
      return {
        id: lastInstance._instanceId,
        needsConfirm: true,
        isPrimaryProtected: lastInstance._instanceId === primaryInstanceId,
      };
    }

    return null;
  }

  function updatePrimaryKeyIfNeeded() {
    if (!primaryRegistrantSelectionKey.value) return;
    const currentKeyIsValid = localSelectionsMap[primaryRegistrantSelectionKey.value]?.qty > 0;

    if (!currentKeyIsValid) {
      if (tables.value.length > 0) {
        const newFirstTable = tables.value[0];
        primaryRegistrantSelectionKey.value = `${newFirstTable.table_id}_${newFirstTable.ticket_id}`;
      } else {
        primaryRegistrantSelectionKey.value = null;
      }
    }
  }

  function handleQtyChange(table, newQty, revert) {
    const key = `${table.table_id}_${table.ticket_id}`;
    const numericQty = !newQty || Number.isNaN(Number(newQty)) ? 0 : Number(newQty);

    if (!localSelectionsMap[key]) {
      localSelectionsMap[key] = { ticket: table, qty: 0 };
    }
    const currentQty = localSelectionsMap[key]?.qty || 0;

    const isLastTable = currentQty === 1 && numericQty === 0;
    const isOnlyTable = tables.value.length === 1;
    const isPrimaryTable = primaryRegistrantSelectionKey.value === key;

    const guestsForKey = additionalTableGuests.value.filter(
      (g) =>
        (hasMultipleTableTypes.value
          ? g.selectedTicketKey
          : primaryRegistrantSelectionKey.value) === key,
    );
    const hasGuests = guestsForKey.length > 0;

    if (isLastTable && isOnlyTable && isPrimaryTable) {
      if (hasGuests) {
        snackbar.setSnackbar(
          `${table.name} has assigned guest forms. Please remove all guests from this table before changing its quantity.`,
          'error',
          5000,
        );
        if (typeof revert === 'function') revert(currentQty);
        return;
      }

      pendingQtyChange.value = {
        key,
        qty: numericQty,
        idsToRemove: [primaryRegistrantTable.value._instanceId],
        isTableGuest: false,
      };
      openDeleteStashedFormConfirm.value = true;
      return;
    }

    if (numericQty > currentQty) {
      localSelectionsMap[key].qty = numericQty;
      const numToAdd = numericQty - currentQty;

      for (let i = 0; i < numToAdd; i++) {
        const instanceId = `${table.table_id}_${table.ticket_id}_${instanceCounter.value++}`;
        orderedTableInstances.value.push({ ...table, _instanceId: instanceId });
      }

      if (!primaryRegistrantSelectionKey.value && orderedTableInstances.value.length > 0) {
        const firstTable = orderedTableInstances.value[0];
        primaryRegistrantSelectionKey.value = `${firstTable.table_id}_${firstTable.ticket_id}`;
      }
      return;
    }

    if (numericQty < currentQty) {
      let filledFormsCount = 0;

      if (primaryRegistrantSelectionKey.value === key && primaryRegistrantTable.value) {
        const primaryStash = stashedData[primaryRegistrantTable.value._instanceId];
        if (primaryStash && Object.keys(primaryStash).length > 0) {
          filledFormsCount++;
        }
      }

      for (const guest of guestsForKey) {
        const guestStash = stashedData[guest._instanceId];
        if (guestStash && Object.keys(guestStash).length > 0) {
          filledFormsCount++;
        }
      }

      const includedTicketCount = table.included_ticket_count || 1;
      const seatsAfterDecrease = numericQty * includedTicketCount;

      if (seatsAfterDecrease < filledFormsCount) {
        snackbar.setSnackbar(
          `Cannot reduce quantity for ${table.name}. You have ${filledFormsCount} filled form(s). Please remove guests manually first.`,
          'warning',
          5000,
        );
        if (typeof revert === 'function') revert(currentQty);
        return;
      }

      const numToRemove = currentQty - numericQty;
      for (let i = 0; i < numToRemove; i++) {
        const instanceToRemove = findBestTableInstanceToRemove(key);
        if (!instanceToRemove) {
          console.error('[Table Logic - Silent] Could not find table instance to remove!');
          if (typeof revert === 'function') revert(currentQty);
          return;
        }
        delete registrations.value[instanceToRemove.id];
        delete stashedData[instanceToRemove.id];
        orderedTableInstances.value = orderedTableInstances.value.filter(
          (t) => t._instanceId !== instanceToRemove.id,
        );
      }

      localSelectionsMap[key].qty = numericQty;
      pendingQtyChange.value = null;
      updatePrimaryKeyIfNeeded();
    }
  }

  function handleDeleteStashedForm() {
    openDeleteStashedFormConfirm.value = false;

    if (pendingDropdownChange.value) {
      const { type, guest, newKey } = pendingDropdownChange.value;

      if (type === 'guest') {
        executeGuestDropdownChange(guest, newKey);
      } else if (type === 'primary') {
        executePrimaryDropdownChange(newKey);
      }

      pendingDropdownChange.value = null;
      return;
    }

    if (pendingQtyChange.value) {
      const { key, qty, idsToRemove, isTableGuest } = pendingQtyChange.value;

      if (idsToRemove && idsToRemove.length > 0) {
        for (const id of idsToRemove) {
          const reg = registrations.value[id];
          if (reg && reg.add_ons && reg.add_ons.length > 0) {
            for (const addon of reg.add_ons) {
              if (occupiedAddons.value[addon.id] && occupiedAddons.value[addon.id] > 0) {
                occupiedAddons.value[addon.id] -= 1;
              }
            }
          }
          delete registrations.value[id];
          delete stashedData[id];
        }

        if (isTableGuest) {
          additionalTableGuests.value = additionalTableGuests.value.filter(
            (t) => !idsToRemove.includes(t._instanceId),
          );
        } else {
          orderedTableInstances.value = orderedTableInstances.value.filter(
            (t) => !idsToRemove.includes(t._instanceId),
          );
          localSelectionsMap[key].qty = qty;
          if (orderedTableInstances.value.length === 0) {
            clearLocalState();
          }
        }
      }

      pendingQtyChange.value = null;
      updatePrimaryKeyIfNeeded();
    }
  }

  function handleCancelDeleteModal() {
    openDeleteStashedFormConfirm.value = false;
    pendingDropdownChange.value = null;
    pendingQtyChange.value = null;
  }

  function handleDeletePrimaryTable(table) {
    if (!table) return;
    const key = `${table.table_id}_${table.ticket_id}`;
    const currentQty = localSelectionsMap[key]?.qty || 0;
    if (currentQty === 0) return;

    const newQty = currentQty - 1;
    const instanceId = table._instanceId;
    const stash = stashedData[instanceId];
    const isFilled = stash && Object.keys(stash).length > 0;

    if (isFilled) {
      pendingQtyChange.value = { key, qty: newQty, idsToRemove: [instanceId], isTableGuest: false };
      openDeleteStashedFormConfirm.value = true;
    } else {
      delete registrations.value[instanceId];
      delete stashedData[instanceId];
      orderedTableInstances.value = orderedTableInstances.value.filter(
        (t) => t._instanceId !== instanceId,
      );
      localSelectionsMap[key].qty = newQty;
      pendingQtyChange.value = null;
      updatePrimaryKeyIfNeeded();
    }
  }

  function clearFormData(instanceId) {
    if (!instanceId) return;

    if (registrations.value[instanceId]) {
      const reg = registrations.value[instanceId];

      if (reg?.add_ons?.length > 0) {
        for (const addon of reg.add_ons) {
          if (occupiedAddons.value[addon.id] && occupiedAddons.value[addon.id] > 0) {
            occupiedAddons.value[addon.id] -= 1;
          }
        }
      }

      delete registrations.value[instanceId];
    }

    if (stashedData[instanceId]) {
      delete stashedData[instanceId];
    }
  }

  function executeGuestDropdownChange(guest, newKey) {
    const oldInstanceId = guest._instanceId;

    clearFormData(oldInstanceId);

    const [tableId, ticketId] = newKey.split('_');
    const newInstanceId = `${tableId}_${ticketId}_${instanceCounter.value++}`;

    guest._instanceId = newInstanceId;
    guest.selectedTicketKey = newKey;
  }

  function executePrimaryDropdownChange(newKey) {
    const oldKey = primaryRegistrantSelectionKey.value;

    if (oldKey) {
      const oldPrimaryInstance = tables.value.find(
        (t) => `${t.table_id}_${t.ticket_id}` === oldKey,
      );
      if (oldPrimaryInstance) {
        clearFormData(oldPrimaryInstance._instanceId);
      }
    }

    primaryRegistrantSelectionKey.value = newKey;

    const newPrimaryInstance = tables.value.find((t) => `${t.table_id}_${t.ticket_id}` === newKey);
    if (newPrimaryInstance) {
      const [tableId, ticketId] = newKey.split('_');
      const newInstanceId = `${tableId}_${ticketId}_${instanceCounter.value++}`;
      newPrimaryInstance._instanceId = newInstanceId;
    }
  }

  function handleTableGuestDropdownChange(guest, newKey) {
    const oldKey = guest.selectedTicketKey;

    if (oldKey === newKey) {
      return;
    }

    const oldInstanceId = guest._instanceId;
    const stash = stashedData[oldInstanceId];
    const isFilled = stash && Object.keys(stash).length > 0;

    if (isFilled) {
      pendingDropdownChange.value = {
        type: 'guest',
        guest: guest,
        newKey: newKey,
      };
      openDeleteStashedFormConfirm.value = true;
      return;
    }

    executeGuestDropdownChange(guest, newKey);
  }

  function handlePrimaryTableDropdownChange(newKey) {
    const oldKey = primaryRegistrantSelectionKey.value;

    if (newKey === oldKey) {
      return;
    }

    if (oldKey) {
      const oldPrimaryInstance = tables.value.find(
        (t) => `${t.table_id}_${t.ticket_id}` === oldKey,
      );
      if (oldPrimaryInstance) {
        const stash = stashedData[oldPrimaryInstance._instanceId];
        const isFilled = stash && Object.keys(stash).length > 0;

        if (isFilled) {
          pendingDropdownChange.value = {
            type: 'primary',
            newKey: newKey,
          };
          openDeleteStashedFormConfirm.value = true;
          return;
        }
      }
    }

    executePrimaryDropdownChange(newKey);
  }

  function addTableGuest() {
    if (remainingTableSeats.value <= 0) {
      snackbar.setSnackbar('No more seats available', 'error', 3000);
      return;
    }

    let selectedTicketKey = null;

    if (!hasMultipleTableTypes.value) {
      selectedTicketKey = primaryRegistrantSelectionKey.value;
    }

    let instanceId;
    if (selectedTicketKey) {
      const [tableId, ticketId] = selectedTicketKey.split('_');
      instanceId = `${tableId}_${ticketId}_${instanceCounter.value++}`;
    } else {
      instanceId = `guest_${tableGuestCounter.value}_${instanceCounter.value++}`;
    }

    additionalTableGuests.value.push({
      _instanceId: instanceId,
      selectedTicketKey: selectedTicketKey,
    });
  }

  function handleDeleteTableGuest(guest) {
    if (!guest) return;

    const instanceId = guest._instanceId;
    const stash = stashedData[instanceId];
    const isFilled = stash && Object.keys(stash).length > 0;

    if (isFilled) {
      pendingQtyChange.value = {
        isTableGuest: true,
        idsToRemove: [instanceId],
      };
      openDeleteStashedFormConfirm.value = true;
    } else {
      additionalTableGuests.value = additionalTableGuests.value.filter(
        (g) => g._instanceId !== instanceId,
      );
      delete registrations.value[instanceId];
      delete stashedData[instanceId];
      pendingQtyChange.value = null;
    }
  }

  const handleRegistrationSave = (tableRegistration, instanceId) => {
    if (!instanceId) return;
    let addonErrors = [];
    const oldRegistration = registrations.value[instanceId];
    if (oldRegistration && oldRegistration.add_ons) {
      oldRegistration.add_ons.forEach((addon) => {
        if (occupiedAddons.value[addon.id] && occupiedAddons.value[addon.id] > 0) {
          occupiedAddons.value[addon.id] -= 1;
        }
      });
    }
    tableRegistration.add_ons?.forEach((addon) => {
      const currentOccupied = occupiedAddons.value[addon.id] || 0;
      const newOccupied = currentOccupied + 1;
      const availableQty = addonSummary.value[addon.id] || 0;
      if (newOccupied > availableQty) {
        const errorMessage = `• ${addon.name} only has ${availableQty} available.`;
        if (!addonErrors.includes(errorMessage)) {
          addonErrors.push(errorMessage);
        }
      }
    });
    if (addonErrors.length > 0) {
      snackbar.setSnackbar(`Cannot save registration:\n${addonErrors.join('\n')}`, 'error', 5000);
      return false;
    }
    tableRegistration.add_ons?.forEach((addon) => {
      if (occupiedAddons.value[addon.id]) {
        occupiedAddons.value[addon.id] += 1;
      } else {
        occupiedAddons.value[addon.id] = 1;
      }
    });

    const ticket_table_ids = instanceId.split('_');
    const instanceTableId =
      ticket_table_ids.length > 0 && !isNaN(Number(ticket_table_ids[0]))
        ? Number(ticket_table_ids[0])
        : null;

    if (instanceTableId) {
      const instanceTable = tables.value.find(
        (t) => t.table_id === instanceTableId && t.ticket_id === tableRegistration.ticket_id,
      );

      if (instanceTable) {
        tableRegistration.table_id = instanceTable.table_id;
      }
    }

    registrations.value[instanceId] = {
      ...tableRegistration,
      _instanceId: instanceId,
    };
    return true;
  };

  const handleStashState = (e, instanceId) => {
    if (!instanceId) return;
    stashedData[instanceId] = e;
  };

  const isSubmitting = ref(false);

  const handleContinueToCheckout = async (opts = {}) => {
    if (isSubmitting.value) return;
    isSubmitting.value = true;

    try {
      const selectionsArray = Object.values(localSelectionsMap)
        .filter((item) => item.qty > 0)
        .map((item) => ({
          ticket: item.ticket,
          qty: item.qty,
        }));

      checkoutStore.setSelections(selectionsArray.length > 0 ? selectionsArray : null, null);

      const primaryRegData = primaryRegistrantTable.value
        ? registrations.value[primaryRegistrantTable.value._instanceId]
        : null;

      const guestRegistrations = Object.values(registrations.value).filter(
        (r) =>
          primaryRegistrantTable.value &&
          r &&
          r._instanceId !== primaryRegistrantTable.value._instanceId,
      );

      const registrationsByTable = {};

      if (primaryRegData) {
        primaryRegData.is_primary = true;
        const primaryTableId = primaryRegData.table_id;
        if (!registrationsByTable[primaryTableId]) {
          registrationsByTable[primaryTableId] = [];
        }
        registrationsByTable[primaryTableId].push(primaryRegData);
      }

      guestRegistrations.forEach((reg) => {
        const tableId = reg.table_id;
        if (!registrationsByTable[tableId]) {
          registrationsByTable[tableId] = [];
        }
        registrationsByTable[tableId].push(reg);
      });

      let payload = {
        sponsor: null,
        invite_code: route.query.invite_code,
        is_group_host: true,
        tickets: null,
        tables: checkoutStore?.selections?.map((t) => ({
          table_id: t.ticket.table_id,
          table_count: t.qty,
          registrations: registrationsByTable[t.ticket.table_id] || [],
        })),
      };

      payload.bill_me_later = !!(opts && opts.bill_me_later);
      payload.total_amount = opts?.total_amount ?? null;
      payload.discount_code = opts?.discount_code || null;
      payload.discount_code_id = opts?.discount_code_id || null;

      await postOrderDetails(payload);
      clearLocalState();
    } catch (error) {
      snackbar.setSnackbar('Failed to submit order. Please try again.', 'error', 5000);
    } finally {
      isSubmitting.value = false;
    }
  };

  watch(loadingTicketData, (isLoading) => {
    if (!isLoading && !isEmpty(allTicketSelectionData.value)) {
      const storeSelections = checkoutStore.selections || [];
      const storeQtyMap = storeSelections.reduce((map, selection) => {
        const key = `${selection.ticket.table_id}_${selection.ticket.ticket_id}`;
        map[key] = selection.qty;
        return map;
      }, {});
      allTicketSelectionData.value.forEach((table) => {
        const key = `${table.table_id}_${table.ticket_id}`;
        const qty = storeQtyMap[key] || 0;
        localSelectionsMap[key] = { ticket: table, qty: qty };
      });

      let initialInstances;
      const allSelections = Object.values(localSelectionsMap)
        .filter((item) => item.qty > 0)
        .map((item) => ({
          ticket: item.ticket,
          qty: item.qty,
        }));
      initialInstances = allSelections.flatMap((t) => {
        return Array.from(Array(t.qty), (_, i) => {
          const instanceId = `${t.ticket.table_id}_${t.ticket.ticket_id}_${instanceCounter.value++}`;
          return { ...t.ticket, _instanceId: instanceId };
        });
      });

      orderedTableInstances.value = initialInstances;

      if (allSelections.length > 0) {
        primaryRegistrantSelectionKey.value = `${allSelections[0].ticket.table_id}_${allSelections[0].ticket.ticket_id}`;
      }
    }
  });

  return {
    // State
    localSelectionsMap,
    registrations,
    stashedData,
    primaryRegistrantSelectionKey,
    openDeleteStashedFormConfirm,
    isSubmitting,
    // Computed
    tables,
    primaryRegistrantTable,
    hasMultipleTableTypes,
    displayAddOnSummary,
    displayTableSummary,
    additionalTableGuests,
    totalTableSeats,
    remainingTableSeats,
    tableGuestDropdownItems,
    disableContinue,
    // Methods
    handleQtyChange,
    handleRegistrationSave,
    handleStashState,
    handleDeletePrimaryTable,
    handleDeleteStashedForm,
    handleCancelDeleteModal,
    handleContinueToCheckout,
    addTableGuest,
    handleDeleteTableGuest,
    handleTableGuestDropdownChange,
    handlePrimaryTableDropdownChange,
  };
}
